import React, { useState } from 'react';
import { Button, Alert, Snackbar } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import SearchIcon from '@mui/icons-material/Search';
import ProspectsUpload from '../components/prospects/ProspectsUpload';
import ProspectsSearch from '../components/prospects/ProspectsSearch';

const Prospects: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'upload' | 'search'>('upload');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleUploadSuccess = () => {
    handleNotification('Prospects uploaded successfully!', 'success');
  };

  const sections = [
    {
      id: 'upload' as const,
      title: 'Prospects Management',
      icon: <UploadIcon />,
      description: 'Upload and manage prospect data'
    },
    {
      id: 'search' as const,
      title: 'Search Prospects',
      icon: <SearchIcon />,
      description: 'Search prospects and generate audience templates'
    }
  ];

  return (
    <>
      {activeSection === 'upload' ? (
        // Standard layout for upload section
        <div className="prospects-container">
          <div className="page-content">
            <h1 className="page-title">Prospects</h1>
            
            {notification.open && (
              <div className={`message ${notification.severity === 'success' ? 'success' : 'error'}`}>
                {notification.message}
              </div>
            )}

            {/* Section Navigation */}
            <div className="upload-section">
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  marginBottom: '1rem',
                  flexWrap: 'wrap'
                }}>
                  {sections.map((section) => (
                    <Button
                      key={section.id}
                      variant={activeSection === section.id ? 'contained' : 'outlined'}
                      onClick={() => setActiveSection(section.id)}
                      startIcon={section.icon}
                      sx={{
                        borderColor: 'var(--e-global-color-secondary)',
                        color: activeSection === section.id ? '#fff' : 'var(--e-global-color-secondary)',
                        backgroundColor: activeSection === section.id ? 'var(--e-global-color-secondary)' : 'transparent',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: activeSection === section.id 
                            ? 'var(--e-global-color-secondary)' 
                            : 'rgba(var(--e-global-color-secondary-rgb), 0.1)',
                          borderColor: 'var(--e-global-color-secondary)'
                        }
                      }}
                    >
                      {section.title}
                    </Button>
                  ))}
                </div>
                
                {/* Section Description */}
                <p style={{ 
                  color: '#666', 
                  fontSize: '0.9rem', 
                  margin: 0,
                  fontStyle: 'italic'
                }}>
                  {sections.find(s => s.id === activeSection)?.description}
                </p>
              </div>

              {/* Section Content */}
              <div className="section-content">
                <ProspectsUpload onUploadSuccess={handleUploadSuccess} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Full-width layout for search section
        <div style={{ width: '100%' }}>
          {/* Navigation bar */}
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            padding: '1rem 2rem', 
            marginBottom: '1rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {sections.map((section) => (
                <Button
                  key={section.id}
                  variant={activeSection === section.id ? 'contained' : 'outlined'}
                  onClick={() => setActiveSection(section.id)}
                  startIcon={section.icon}
                  sx={{
                    borderColor: 'var(--e-global-color-secondary)',
                    color: activeSection === section.id ? '#fff' : 'var(--e-global-color-secondary)',
                    backgroundColor: activeSection === section.id ? 'var(--e-global-color-secondary)' : 'transparent',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: activeSection === section.id 
                        ? 'var(--e-global-color-secondary)' 
                        : 'rgba(var(--e-global-color-secondary-rgb), 0.1)',
                      borderColor: 'var(--e-global-color-secondary)'
                    }
                  }}
                >
                  {section.title}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Full-width search component */}
          <ProspectsSearch onNotification={handleNotification} />
        </div>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Prospects;
